import type { ApiClient } from '@classgo/app-kit';
import type {
  AlbertDraftInput,
  AlbertDraftResult,
  AlbertLessonPlanInput,
  AlbertLessonPlanResult,
  AlbertLessonPlansView,
  AlbertLessonPlanUpdate,
  AlbertLessonPlanView,
  AlbertMaterialInput,
  AlbertMaterialResult,
  AlbertMaterialView,
  MyClassesView,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * Albert's write side (#118 over the API of #112): a plan generated and saved on a topic, the
 * teacher's edits on it, a document with its download link, a fresh link for a document, and a
 * draft from bullet points. Refusals arrive as `ApiError` with `details.reason` or
 * `details.flagged`; `albertRefusal` turns them into messages.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const albertApi = (api: ApiClient) => ({
  generatePlan: (body: AlbertLessonPlanInput) =>
    api.request<AlbertLessonPlanResult>('/albert/lesson-plans', { method: 'POST', body }),
  savePlan: (id: string, body: AlbertLessonPlanUpdate) =>
    api.request<AlbertLessonPlanView>(`/albert/lesson-plans/${id}`, { method: 'PUT', body }),
  generateMaterial: (body: AlbertMaterialInput) =>
    api.request<AlbertMaterialResult>('/albert/materials', { method: 'POST', body }),
  refreshMaterial: (id: string) => api.request<AlbertMaterialView>(`/albert/materials/${id}`),
  writeDraft: (body: AlbertDraftInput) =>
    api.request<AlbertDraftResult>('/albert/drafts', { method: 'POST', body }),
});

/**
 * Albert's reads: the caller's teaching pairs this year (`GET /me/classes`) and the plans saved
 * on a topic with the quota left (`GET /albert/lesson-plans?topicId`).
 */
export const albertQueries = {
  classes: (api: ApiClient) =>
    queryOptions({
      queryKey: ['me', 'classes'],
      queryFn: () => api.request<MyClassesView>('/me/classes'),
    }),
  plans: (api: ApiClient, topicId: string) =>
    queryOptions({
      queryKey: ['albert', 'lesson-plans', topicId],
      queryFn: () =>
        api.request<AlbertLessonPlansView>(
          `/albert/lesson-plans?${new URLSearchParams({ topicId }).toString()}`,
        ),
      enabled: topicId !== '',
    }),
};
